/**
 * All landing-page copy lives here so marketing edits never require touching
 * component code. Icon names map to lucide-react exports (see icons.ts).
 */

export const hero = {
  eyebrow: 'AI voice relationship management',
  title: 'Your customers will tell you everything. If someone actually asks.',
  subtitle:
    'VoxDiscover runs natural AI voice interviews at scale, then turns every conversation into personas, segments and campaigns your team can act on the same day.',
  primaryCta: { label: 'Request a demo', href: '#contact' },
  secondaryCta: { label: 'See how it works', href: '#how-it-works' },
  stats: [
    { value: '5 min', label: 'Average interview length' },
    { value: '24/7', label: 'Always-on outreach' },
    { value: '100%', label: 'Conversations transcribed and scored' },
  ],
} as const

export const steps = [
  {
    icon: 'PhoneCall',
    title: 'Interview',
    body: 'An AI voice agent calls or web-links your customers and holds a real conversation — following up, probing, adapting to what it hears.',
  },
  {
    icon: 'FileAudio',
    title: 'Transcribe',
    body: 'Every call is transcribed, diarised and embedded. Nothing depends on someone remembering to write notes afterwards.',
  },
  {
    icon: 'Sparkles',
    title: 'Synthesise',
    body: 'Clustering and LLM analysis turn raw transcripts into personas, themes and relationship-health scores you can compare over time.',
  },
  {
    icon: 'Rocket',
    title: 'Act',
    body: 'Route the intelligence straight into campaigns, experiments and orchestrated outreach — then measure what moved.',
  },
] as const

export const features = [
  {
    icon: 'Mic',
    title: 'Natural voice interviews',
    body: 'Low-latency speech-to-speech agents that interrupt gracefully, handle tangents and sound like a person, not a phone tree.',
  },
  {
    icon: 'Users',
    title: 'Auto-generated personas',
    body: 'Transcripts are embedded and clustered, then written up as named personas with evidence quotes attached to each trait.',
  },
  {
    icon: 'Gauge',
    title: 'Relationship health scoring',
    body: 'Every customer gets an RTI score that tracks sentiment, engagement and risk across the whole conversation history.',
  },
  {
    icon: 'PieChart',
    title: 'Segment analytics',
    body: 'Slice your base by persona, score or theme and watch how each segment moves after a campaign lands.',
  },
  {
    icon: 'Workflow',
    title: 'Campaign orchestration',
    body: 'Turn an insight into an outreach campaign in a couple of clicks, with experiments and variant assignment built in.',
  },
  {
    icon: 'ShieldCheck',
    title: 'Consent and control',
    body: 'Recording consent, retention windows and per-customer opt-outs are first-class, not bolted on afterwards.',
  },
] as const

export const useCases = [
  {
    title: 'Churn diagnosis',
    body: 'Interview every account that cancels and find the real reason — in their words, at a volume no human team could cover.',
  },
  {
    title: 'Win/loss research',
    body: 'Call closed-lost prospects within days, while the decision is still fresh, and feed the patterns back to sales.',
  },
  {
    title: 'Product discovery',
    body: 'Run continuous discovery interviews across the whole base instead of the same twelve friendly customers.',
  },
  {
    title: 'Onboarding check-ins',
    body: 'Catch a stalled rollout in week two rather than at the renewal conversation eleven months later.',
  },
] as const

export const faqs = [
  {
    question: 'Do customers realise they are talking to an AI?',
    answer:
      'Yes — the agent discloses it at the start of every conversation, and consent for recording is captured before anything is stored.',
  },
  {
    question: 'How long does an interview take?',
    answer:
      'Most run three to seven minutes. You set the question guide and the maximum length; the agent adapts within those bounds.',
  },
  {
    question: 'What happens to the recordings?',
    answer:
      'Audio and transcripts are stored against the customer record with a retention window you configure. Customers can opt out at any time.',
  },
  {
    question: 'Can it work with our existing customer IDs?',
    answer:
      'Yes. VoxDiscover accepts both internal UUIDs and external identifiers from your own systems, and keeps them linked throughout the pipeline.',
  },
  {
    question: 'How do we get the intelligence out?',
    answer:
      'Through the portal dashboards, or programmatically — personas, scores and segments are all available via the API.',
  },
] as const
